import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'
import echarts from '../config'
import { cleanChartConfig } from '../utils'

/**
 * Replaces a gradient color with an echarts LinearGradient
 * @param subject - The style object that may contain a gradient
 * @returns {(*&{color: LinearGradient})|*|undefined|null} The style with gradient replaced or unchanged
 */
function replaceGradient (subject) {
  if (typeof subject === 'undefined') return undefined
  if (subject === null) return null

  if (subject?.color === 'gradient') {
    return {
      ...subject,
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, subject.gradientColorStops || [])
    }
  }

  return subject
}

function ComponentLine ({
  seriesId,
  name,
  data,
  colorBy,
  xAxisIndex,
  yAxisIndex,
  polarIndex,
  symbol,
  symbolSize,
  symbolRotate,
  symbolKeepAround,
  symbolOffset,
  showSymbol,
  showAllSymbol,
  legendHoverLink,
  stack,
  stackStrategy,
  cursor,
  connectNulls,
  clip,
  triggerLineEvent,
  step,
  label,
  endLabel,
  labelLine,
  labelLayout,
  itemStyle,
  lineStyle,
  areaStyle,
  emphasis,
  blur,
  select,
  selectedMode,
  smooth,
  smoothMonotone,
  sampling,
  dimensions,
  encode,
  seriesLayoutBy,
  datasetIndex,
  dataGroupId,
  markPoint,
  markLine,
  markArea,
  zlevel,
  z,
  silent,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationDelayUpdate,
  animationEasingUpdate,
  universalTransition,
  tooltip
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    setSeries(seriesId, cleanChartConfig({
      type: 'line',
      id: seriesId,
      name,
      data,
      colorBy,
      // coordinateSystem,
      xAxisIndex,
      yAxisIndex,
      polarIndex,
      symbol,
      symbolSize,
      symbolRotate,
      symbolKeepAround,
      symbolOffset,
      showSymbol,
      showAllSymbol,
      legendHoverLink,
      stack,
      stackStrategy,
      cursor,
      connectNulls,
      clip,
      triggerLineEvent,
      step,
      label,
      endLabel,
      labelLine,
      labelLayout,
      itemStyle,
      lineStyle,
      areaStyle: replaceGradient(areaStyle),
      emphasis,
      blur,
      select,
      selectedMode,
      smooth,
      smoothMonotone,
      sampling,
      dimensions,
      encode,
      seriesLayoutBy,
      datasetIndex,
      dataGroupId,
      markPoint,
      markLine,
      markArea,
      zlevel,
      z,
      silent,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationDelayUpdate,
      animationEasingUpdate,
      universalTransition,
      tooltip
    }))
  }, [
    seriesId,
    name,
    data,
    colorBy,
    // coordinateSystem,
    xAxisIndex,
    yAxisIndex,
    polarIndex,
    symbol,
    symbolSize,
    symbolRotate,
    symbolKeepAround,
    symbolOffset,
    showSymbol,
    showAllSymbol,
    legendHoverLink,
    stack,
    stackStrategy,
    cursor,
    connectNulls,
    clip,
    triggerLineEvent,
    step,
    label,
    endLabel,
    labelLine,
    labelLayout,
    itemStyle,
    lineStyle,
    areaStyle,
    emphasis,
    blur,
    select,
    selectedMode,
    smooth,
    smoothMonotone,
    sampling,
    dimensions,
    encode,
    seriesLayoutBy,
    datasetIndex,
    dataGroupId,
    markPoint,
    markLine,
    markArea,
    zlevel,
    z,
    silent,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationDelayUpdate,
    animationEasingUpdate,
    universalTransition,
    tooltip,
    setSeries
  ])

  return null
}

ComponentLine.propTypes = {
  seriesId: PropTypes.string,
  name: PropTypes.string,
  data: PropTypes.array,

  /** The policy to take color from option.color. Valid values:

   'series': assigns the colors in the palette by series, so that all data in the same series are in the same color;
   'data': assigns colors in the palette according to data items, with each data item using a different color. */
  colorBy: PropTypes.oneOf(['series', 'data']),

  /** The coordinate used in the series, whose options are:
   'cartesian2d'
   Use a two-dimensional rectangular coordinate (also known as Cartesian coordinate), with xAxisIndex and yAxisIndex to assign the corresponding axis component.
   'polar'
   Use polar coordinates, with polarIndex to assign the corresponding polar coordinate component. */
  // coordinateSystem: PropTypes.oneOf(['cartesian2d', 'polar']),

  /** Index of x axis to combine with, which is useful for multiple x axes in one chart. */
  xAxisIndex: PropTypes.number,

  /** Index of y axis to combine with, which is useful for multiple y axes in one chart. */
  yAxisIndex: PropTypes.number,

  /** Index of polar coordinate to combine with, which is useful for multiple polar axes in one chart. */
  polarIndex: PropTypes.number,

  /**
   * Symbol of .
   * Icon types provided by ECharts includes
   * 'circle', 'rect', 'roundRect', 'triangle', 'diamond', 'pin', 'arrow', 'none'
   * It can be set to an image with 'image://url' , in which URL is the link to an image, or dataURI of an image.
   */
  symbol: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf(['circle', 'rect', 'roundRect', 'triangle', 'diamond', 'pin', 'arrow', 'none'])]),

  /** symbol size. It can be set to single numbers like 10, or use an array to represent width and height. For example, [20, 10] means symbol width is 20, and height is10.
   If size of symbols needs to be different, you can set with callback function in the following format: */
  symbolSize: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),

  /** Rotate degree of symbol. The negative value represents clockwise. Note that when symbol is set to be 'arrow' in markLine, symbolRotate value will be ignored, and compulsively use tangent angle. */
  symbolRotate: PropTypes.number,

  /** Whether to keep aspect for symbols in the form of path://. */
  symbolKeepAround: PropTypes.bool,

  /** Offset of symbol relative to original position. By default, symbol will be put in the center position of data.
   *  But if symbol is from user-defined vector path or image, you may not expect symbol to be in center. In this case,
   *  you may use this attribute to set offset to default position. It can be in absolute pixel value, or in relative percentage value. */
  symbolOffset: PropTypes.array,

  /** Whether to show symbol. It would be shown during tooltip hover. */
  showSymbol: PropTypes.bool,

  /** Only work when main axis is 'category' axis (axis.type is 'category'). Optional values:
   'auto': Default value. Show all symbols if there is enough space. Otherwise follow the interval strategy with with axisLabel.interval.
   true: Show all symbols.
   false: Follow the interval strategy with axisLabel.interval. */
  showAllSymbol: PropTypes.bool,

  /** Whether to enable highlighting chart when legend is being hovered. */
  legendHoverLink: PropTypes.bool,

  /** If stack the value. On the same category axis, the series with the same stack name would be put on top of each other.
   See also stackStrategy on how to customize how values are stacked.
   Notice: stack only supports stacking on value and log axis for now. time and category axis are not supported. */
  stack: PropTypes.string,

  /** How to stack values if the stack property has been set. Options:
   'samesign': only stack values if the value to be stacked has the same sign as the currently cumulated stacked value.
   'all': stack all values, irrespective of the signs of the current or cumulative stacked value.
   'positive': only stack positive values.
   'negative': only stack negative values. */
  stackStrategy: PropTypes.oneOf(['samesign', 'all', 'positive', 'negative']),

  /** The mouse style when mouse hovers on an element, the same as cursor property in CSS. */
  cursor: PropTypes.string,

  /** Whether to connect the line across null points. */
  connectNulls: PropTypes.bool,

  /** If clip the overflow on the coordinate system. Clip results varies between series */
  clip: PropTypes.bool,

  /** Whether line and area can trigger the event. */
  triggerLineEvent: PropTypes.bool,

  /** Whether to show as a step line. It can be true, false. Or 'start', 'middle', 'end'. Which will configure the turn point of step line. */
  step: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['start', 'middle', 'end'])]),

  /** Text label of , to explain some data information about graphic item like value, name and so on. label is placed under itemStyle in ECharts 2.x.
   *  In ECharts 3, to make the configuration structure flatter, labelis taken to be at the same level with itemStyle, and has emphasis as itemStyle does. */
  label: PropTypes.object,

  /** Label on the end of line. */
  endLabel: PropTypes.object,

  /** Configuration of label guide line. */
  labelLine: PropTypes.object,

  /** Unified layout configuration of labels. */
  labelLayout: PropTypes.object,

  /** The style of the symbol point of broken line. */
  itemStyle: PropTypes.object,

  /** Line style. */
  lineStyle: PropTypes.object,

  /** The style of area. */
  areaStyle: PropTypes.object,

  /** Highlight style of the graphic. */
  emphasis: PropTypes.object,

  /** Configurations of blur state. Available when emphasis.focus is set. */
  blur: PropTypes.object,

  /** Configurations of select state. Available when selectedMode is set. */
  select: PropTypes.object,

  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.
   Besides, it can be set to 'single', 'multiple' or 'series', for single selection, multiple selections and whole series selection. */
  selectedMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['single', 'multiple', 'series'])]),

  /** Whether to show as smooth curve.
   If is typed in boolean, then it means whether to enable smoothing. If is typed in number, valued from 0 to 1, then it means smoothness. A smaller value makes it less smooth */
  smooth: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),

  /** Whether the broken line keep the monotonicity when it is smoothed. It can be set as 'x', 'y' to keep the monotonicity on x axis or y axis. */
  smoothMonotone: PropTypes.oneOf(['x', 'y']),

  /** The downsampling strategy used when the data size is much larger than pixel size. It will improve the performance when turned on. Defaults to be turned off, indicating that all the data points will be drawn. */
  sampling: PropTypes.oneOf(['lttb', 'average', 'min', 'max', 'minmax', 'sum']),

  /** dimensions can be used to define dimension info for series.data or dataset.source. */
  dimensions: PropTypes.array,

  /** Define what is encoded to for each dimension of data */
  encode: PropTypes.object,

  /** When dataset is used, seriesLayoutBy specifies whether the column or the row of dataset is mapped to the series, namely, the series is "layout" on columns or rows. Optional values:
   'column': by default, the columns of dataset are mapped the series. In this case, each column represents a dimension.
   'row'：the rows of dataset are mapped to the series. In this case, each row represents a dimension. */
  seriesLayoutBy: PropTypes.oneOf(['column', 'row']),

  /** If series.data is not specified, and dataset exists, the series will use dataset. datasetIndex specifies which dataset will be used. */
  datasetIndex: PropTypes.number,

  /** A group ID assigned to all data items in the series.
   This option has a lower priority than groupId, which means when groupId is specified for a certain data item the
   dataGroupId will be simply ignored for that data item. For more information, please see series.data.groupId. */
  dataGroupId: PropTypes.string,

  /** Mark point in a chart. */
  markPoint: PropTypes.object,

  /** Use a line in the chart to illustrate. */
  markLine: PropTypes.object,

  /** Used to mark an area in chart. For example, mark a time interval. */
  markArea: PropTypes.object,

  /** zlevel value of all graphical elements in Line.
   zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in
   different Canvases, which is a common optimization technique. We can put those frequently changed elements
   (like those with animations) to a separate zlevel. Notice that too many Canvases will increase memory cost,
   and should be used carefully on mobile phones to avoid crash.
   Canvases with bigger zlevel will be placed on Canvases with smaller zlevel. */
  zlevel: PropTypes.number,

  /** z value of all graphical elements in Line, which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.
   z has a lower priority to zlevel, and will not create new Canvas. */
  z: PropTypes.number,

  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. */
  silent: PropTypes.bool,

  /** Whether to enable animation. */
  animation: PropTypes.bool,

  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. */
  animationThreshold: PropTypes.number,

  /** Duration of the first animation, which supports callback function for different data to have different animation effect: */
  animationDuration: PropTypes.number,

  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. */
  animationEasing: PropTypes.string,

  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. */
  animationDelay: PropTypes.number,

  /** Time for animation to complete, which supports callback function for different data to have different animation effect: */
  animationDurationUpdate: PropTypes.number,

  /** Easing method used for animation. */
  animationEasingUpdate: PropTypes.number,

  /** Delay before updating animation, which supports callback function for different data to have different animation effects. */
  animationDelayUpdate: PropTypes.number,

  universalTransition: PropTypes.object,

  tooltip: PropTypes.object
}

ComponentLine.defaultProps = {
  showSymbol: false,
  symbol: 'none'
}

export default ComponentLine
